
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Star, ThumbsUp, Flag, MessageSquare } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface Review {
  id: string;
  rating: number;
  text?: string;
  created_at: string;
  student_id: string;
  listing_id: string;
  student_name?: string;
  student_avatar?: string;
  photos?: string[];
  helpful_count?: number;
  user_found_helpful?: boolean;
}

interface ReviewSystemProps {
  listingId: string;
  canReview?: boolean;
}

const ReviewSystem: React.FC<ReviewSystemProps> = ({ listingId, canReview = false }) => {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [rating, setRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReviews();
  }, [listingId]);

  const fetchReviews = async () => {
    try {
      // First get reviews
      const { data: reviewsData, error: reviewsError } = await supabase
        .from('reviews')
        .select(`
          id,
          rating,
          text,
          created_at,
          student_id,
          listing_id,
          photos
        `)
        .eq('listing_id', listingId)
        .order('created_at', { ascending: false });

      if (reviewsError) throw reviewsError;

      // Then get profile data for each review
      const formattedReviews: Review[] = [];
      if (reviewsData) {
        for (const review of reviewsData) {
          const { data: profileData } = await supabase
            .from('profiles')
            .select('full_name, avatar_url')
            .eq('id', review.student_id)
            .single();

          formattedReviews.push({
            ...review,
            student_name: profileData?.full_name || 'Anonymous',
            student_avatar: profileData?.avatar_url,
            helpful_count: Math.floor(Math.random() * 10), // Mock data
            user_found_helpful: false // Mock data
          });
        }
      }

      setReviews(formattedReviews);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const submitReview = async () => {
    if (!rating) {
      toast.error(t('rating_required', 'التقييم مطلوب', 'Rating is required'));
      return;
    }

    setSubmitting(true);
    try {
      const { error } = await supabase
        .from('reviews')
        .insert({
          listing_id: listingId,
          student_id: user?.id,
          rating,
          text: reviewText.trim() || null
        });

      if (error) throw error;

      toast.success(t('review_submitted', 'تم إرسال التقييم', 'Review submitted successfully'));
      setRating(0);
      setReviewText('');
      fetchReviews();
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error(t('error_submitting_review', 'خطأ في إرسال التقييم', 'Error submitting review'));
    } finally {
      setSubmitting(false);
    }
  };

  const markHelpful = async (reviewId: string) => {
    // This would be implemented with a proper helpful_reviews table
    toast.info(t('feature_coming_soon', 'هذه الميزة قادمة قريباً', 'Feature coming soon'));
  };

  const reportReview = async (reviewId: string) => {
    try {
      const { error } = await supabase
        .from('reports')
        .insert({
          target_type: 'review',
          target_id: reviewId,
          reporter_id: user?.id,
          reason_code: 'inappropriate_content'
        });

      if (error) throw error;
      toast.success(t('review_reported', 'تم الإبلاغ عن التقييم', 'Review reported'));
    } catch (error) {
      console.error('Error reporting review:', error);
      toast.error(t('error_reporting', 'خطأ في الإبلاغ', 'Error reporting'));
    }
  };

  const StarRating: React.FC<{ rating: number; onRatingChange?: (rating: number) => void; interactive?: boolean }> = ({ 
    rating, 
    onRatingChange, 
    interactive = false 
  }) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-5 w-5 ${interactive ? 'cursor-pointer' : ''} ${
              star <= rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
            }`}
            onClick={() => interactive && onRatingChange?.(star)}
          />
        ))}
      </div>
    );
  };

  const averageRating = reviews.length > 0 
    ? reviews.reduce((sum, review) => sum + review.rating, 0) / reviews.length 
    : 0;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Star className="h-5 w-5" />
            {t('reviews_ratings', 'التقييمات والمراجعات', 'Reviews & Ratings')}
          </CardTitle>
          <CardDescription>
            {reviews.length > 0 && (
              <div className="flex items-center gap-2">
                <StarRating rating={Math.round(averageRating)} />
                <span className="font-medium">{averageRating.toFixed(1)}</span>
                <span className="text-muted-foreground">
                  ({reviews.length} {t('reviews', 'تقييم', 'reviews')})
                </span>
              </div>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {canReview && user && (
            <div className="space-y-4 p-4 border rounded-lg mb-6">
              <h4 className="font-medium">{t('write_review', 'اكتب تقييماً', 'Write a Review')}</h4>
              <div>
                <label className="text-sm font-medium mb-2 block">
                  {t('your_rating', 'تقييمك', 'Your Rating')}
                </label>
                <StarRating rating={rating} onRatingChange={setRating} interactive />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">
                  {t('your_review', 'مراجعتك', 'Your Review')} ({t('optional', 'اختياري', 'Optional')})
                </label>
                <Textarea
                  value={reviewText}
                  onChange={(e) => setReviewText(e.target.value)}
                  placeholder={t('share_experience', 'شاركنا تجربتك...', 'Share your experience...')}
                  rows={3}
                />
              </div>
              <Button onClick={submitReview} disabled={submitting || !rating}>
                {submitting 
                  ? t('submitting', 'جاري الإرسال...', 'Submitting...') 
                  : t('submit_review', 'إرسال التقييم', 'Submit Review')
                }
              </Button>
            </div>
          )}

          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3].map(i => (
                <div key={i} className="p-4 border rounded-lg">
                  <div className="flex items-start gap-3">
                    <div className="h-10 w-10 bg-muted rounded-full animate-pulse" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 bg-muted animate-pulse rounded w-1/4" />
                      <div className="h-3 bg-muted animate-pulse rounded w-1/2" />
                      <div className="h-16 bg-muted animate-pulse rounded" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-8">
              <MessageSquare className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">
                {t('no_reviews_yet', 'لا توجد تقييمات بعد', 'No reviews yet')}
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                {t('be_first_review', 'كن أول من يقيم هذا العقار', 'Be the first to review this property')}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {reviews.map((review) => (
                <div key={review.id} className="p-4 border rounded-lg">
                  <div className="flex items-start gap-3">
                    <Avatar>
                      <AvatarImage src={review.student_avatar} />
                      <AvatarFallback>
                        {review.student_name?.charAt(0) || '?'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="font-medium">{review.student_name}</span>
                        <StarRating rating={review.rating} />
                        <span className="text-sm text-muted-foreground">
                          {new Date(review.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      {review.text && (
                        <p className="text-sm mb-3">{review.text}</p>
                      )}
                      {review.photos && review.photos.length > 0 && (
                        <div className="flex gap-2 mb-3">
                          {review.photos.slice(0, 3).map((photo, index) => (
                            <img
                              key={index}
                              src={photo}
                              alt="Review photo"
                              className="h-16 w-16 object-cover rounded"
                            />
                          ))}
                          {review.photos.length > 3 && (
                            <div className="h-16 w-16 bg-muted rounded flex items-center justify-center text-sm">
                              +{review.photos.length - 3}
                            </div>
                          )}
                        </div>
                      )}
                      <div className="flex items-center gap-4 text-sm">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => markHelpful(review.id)}
                          className="text-muted-foreground hover:text-foreground"
                        >
                          <ThumbsUp className="h-3 w-3 mr-1" />
                          {t('helpful', 'مفيد', 'Helpful')} ({review.helpful_count})
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => reportReview(review.id)}
                          className="text-muted-foreground hover:text-red-500"
                        >
                          <Flag className="h-3 w-3 mr-1" />
                          {t('report', 'إبلاغ', 'Report')}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ReviewSystem;
